<template>
  <div class="app">
    <ErrorView :errorMessage="error" @clearError="clearError" />
    <VerificationPopup v-if="testing" :record="test" :testing="testing" :request='request' :cache='cache' />
    <LoadingScreen :isVisible="isLoading" />
    <LoginPage v-if='needLogin()' :icons='icons'/>
    <TfAuth v-if='needQR()' :request='request' :userName="userName" />
    <TfVerify v-if='need2FA()' />
    <DashboardPage
        v-if='loggedIn'
        :icons='icons'
        :role='role'
        :records="records"
        :cache="cache"
        :request="request"
        @passOperation='passOperation'
    />
  </div>
</template>

<script>

import EventBus from '@/utils/EventBus';
import {IconsUI} from '@/utils/IconsUI';
import TfAuth from "@/pages/TfAuth.vue";
import TfVerify from "@/pages/TfVerify.vue";
import LoginPage from '@/pages/LoginPage.vue';
import DashboardPage from '@/pages/DashboardPage.vue';
import ErrorView from '@/components/ui/ErrorView.vue';
import LoadingScreen from "@/pages/LoadingScreen.vue";
import VerificationPopup from "@/components/ui/ViewProfile.vue";
import {LoginUser, SetNew2FA, userLoggedIn, Verify2FA} from '@/tools/login_tools';

export default {
  name: "App",
  inject: ['query', 'cache', 'config', 'request', 'crypto'],
  components: {
    VerificationPopup,
    LoadingScreen,
    TfVerify,
    TfAuth,
    DashboardPage,
    LoginPage,
    ErrorView,
  },
  data() {
    return {
      cred: {},
      error: null,
      testing: false,
      can2FA: false,
      role: -1,
      loggedIn: false,
      userName: '',
      records: [],
      isLoading: false,
      icons: new IconsUI(),
      test: {
        dob: "24-02-1994",
        uid: 1,
        gps: "30.5469429, 104.0698698",
        gender: "Female",
        lastLogin: "05-07-2025",
        fullName: "HANIA AMIR",
        languages: "id",
        residence: "ID",
        nationality: "ID"
      },
      // files: [
      //   "idback.jpg",
      //   "idfront.jpg",
      //   "idhand.jpg",
      //   "passport.jpg",
      //   "selfie.mp4"
      // ]
    };
  },
  async mounted() {
    await this.start();
  },
  methods: {
    async start() {
      await this.autoLogin();
    },
    async autoLogin() {
      this.setLoading(true);
      try {
        let data = this.query.get('data');
        let token = this.query.get('token');
        if (data && token) await LoginUser(this, data, token);
        if (userLoggedIn(this.userData)) return;
        const loginCache = this.cache.loginCache();
        data = loginCache.data;
        token = loginCache.token;
        if (data && token) await LoginUser(this, data, token);
      } catch (error) {
        this.setError(error);
      } finally {
        this.setLoading(false);
      }
    },
    async afterLoggedIn(userName) {
      const userData = this.userData || {};
      this.can2FA = userData.can2FA;
      this.userName = userName || '';
      this.role = userData.role || -1;
    },
    async loginClicked(loginInfo) {
      this.setLoading(true);
      try {
        let data = loginInfo.data;
        let password = loginInfo.password;
        if (!data || !password) return;
        await LoginUser(this, data, password, true);
      } catch (error) {
        this.setError(error);
      } finally {
        this.setLoading(false);
      }
    },
    async newTFAClicked(data) {
      this.setLoading(true);
      try {
        const secret = data.secret;
        this.can2FA = await SetNew2FA(this, this.userName, secret);
      } catch (error) {
        this.setError(error);
      } finally {
        this.setLoading(false);
      }
    },
    async verify2FA(data) {
      this.setLoading(true);
      try {
        const code = data.code;
        const verData = await Verify2FA(this, this.userName, code);
        this.loggedIn = !!verData;
        console.log("DONE", verData);
        this.records = verData ? (verData.pending || []) : [];
        if(!this.records || !this.records.length) this.records = [];
      } catch (error) {
        this.setError(error);
      } finally {
        this.setLoading(false);
      }
    },
    async completedRecord(info) {
      try {
        const type = info.type;
        const record = info.record;
        if(type === "ignore") {
          return this.removeRecord(record, 'This user already verified by someone');
        }
        const loginCache = this.cache.loginCache();
        const uid = this.userData.uid;
        const token = loginCache.token;
        const userName = loginCache.data;
        const verifyInfo = info.verifyInfo;
        const response = await this.request.verifyUserProfile(uid, userName, token, verifyInfo);
        if(response.msg === "SUCCESS") {
          return this.removeRecord(record, 'SUCCESS');
        }
        return this.setError(response.msg);
      } catch (error) {
        this.setError(error);
      }
    },
    removeRecord(record, msg) {
      const existingRecordIndex = this.records.findIndex(r => r.uid === record.uid);
      if (existingRecordIndex !== -1) {
        this.records.splice(existingRecordIndex, 1);
      }
      return this.setError(msg);
    },
    async passOperation(info) {
      console.log("operation", info)
    },
    setLoading(value) {
      this.isLoading = value;
    },
    logoutUser() {
      this.cache.remove('login');
      this.userData = {};
    },
    setError(msg) {
      this.error = msg;
    },
    clearError() {
      this.error = null;
    },
    need2FA() {
      if(this.loggedIn || this.role < 1) return false;
      return this.can2FA;
    },
    needQR() {
      if(this.loggedIn || this.role < 1) return false;
      return !this.can2FA;
    },
    needLogin() {
      // const userData = this.userData;
      return this.role < 0;
    }
  },
  beforeMount() {
    EventBus.on('setError', this.setError);
    EventBus.on('verify2FA', this.verify2FA);
    EventBus.on('logoutUser', this.logoutUser);
    EventBus.on('setLoading', this.setLoading);
    EventBus.on('loginClicked', this.loginClicked);
    EventBus.on('newTFAClicked', this.newTFAClicked);
    EventBus.on('completedRecord', this.completedRecord);
  },
  beforeUnmount() {
    EventBus.off('setError', this.setError);
    EventBus.off('verify2FA', this.verify2FA);
    EventBus.off('setLoading', this.setLoading);
    EventBus.off('logoutUser', this.logoutUser);
    EventBus.off('loginClicked', this.loginClicked);
    EventBus.off('newTFAClicked', this.newTFAClicked);
    EventBus.off('completedRecord', this.completedRecord);
  },
};
</script>
